import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, User, Shield, Users, Zap, Lock, LogIn, ArrowRight, Heart } from "lucide-react";
import PersonForm from "../components/PersonForm";
import OrgForm from "../components/OrgForm";
import Modal from "../components/Modal";
import { LOGO_URL } from "../data/formOptions";
import { api } from "../lib/api";

export default function Landing() {
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [referrer, setReferrer] = useState(null);
  const [showForm, setShowForm] = useState(null); // "org" | "person" | null

  useEffect(() => {
    if (!refCode) return;
    api
      .get(`/referrals/resolve`, { params: { code: refCode } })
      .then((r) => setReferrer(r.data))
      .catch(() => setReferrer(false));
  }, [refCode]);

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-[#050d1a]" data-testid="landing-page">
      {/* Hero background — centered logo watermark */}
      <div
        className="fixed inset-0 z-0 bg-no-repeat bg-center opacity-60 pointer-events-none"
        style={{ backgroundImage: `url(${LOGO_URL})`, backgroundSize: "min(720px, 70vw)" }}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(5,13,26,0.25) 0%, rgba(5,13,26,0.85) 65%, #050d1a 100%)" }}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-0 csc-grid-bg opacity-[0.35] pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Navbar */}
        <header
          className="px-6 lg:px-10 py-4 flex items-center justify-between"
          style={{
            background: "rgba(5, 13, 26, 0.9)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img src={LOGO_URL} alt="CyberSecurity4Charities.org" className="h-[72px] w-[72px] object-contain" />
            <span className="font-semibold text-base sm:text-xl">
              <span style={{ color: "#ffffff" }}>CyberSecurity</span><span style={{ color: "#39ff14" }}>4</span><span style={{ color: "#00aaff" }}>Charities</span><span style={{ color: "#39ff14" }}>.Org</span>
            </span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/about" className="csc-nav-btn csc-nav-btn-outline" data-testid="nav-about">About Us</Link>
            <Link to="/donate" className="csc-nav-btn csc-nav-btn-primary" data-testid="nav-donate"><Heart size={14} /> Donate</Link>
            <Link to="/login" className="csc-nav-btn csc-nav-btn-secondary" data-testid="nav-login"><LogIn size={14} /> Sign In</Link>
          </nav>
        </header>

        {/* Hero */}
        <main className="flex-1 flex flex-col items-center px-6 pt-12 sm:pt-20 pb-20 text-center">
          <div className="csc-pill inline-flex" data-testid="hero-pill">
            <Lock size={13} /> Protecting Non-Profits Worldwide
          </div>

          <h1 className="mt-8 font-extrabold text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.05] max-w-5xl">
            <span style={{ color: "#ffffff" }}>CyberSecurity</span>
            <span style={{ color: "#00aaff" }}> Support</span>
            <span style={{ color: "#39ff14" }}> For</span>
            <span style={{ color: "#00aaff" }}> Charities</span>
            <br />
            <span style={{ color: "#39ff14" }}>&amp; Non-Profits</span>
          </h1>

          <p className="mt-5 text-[#94a3b8] text-base sm:text-lg max-w-xl">
            Protecting the organizations that protect people.
          </p>

          {referrer && (
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-md border border-[#39ff14]/40 bg-[#39ff14]/10" data-testid="referral-banner">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#39ff14]">Invited by</span>
              <span className="text-white text-sm font-medium">{referrer.organization_name}</span>
              <span className="font-mono-tech text-[10px] text-[#94a3b8]">code · {referrer.ref_code}</span>
            </div>
          )}

          {/* Two registration cards — pushed to far left + right to reveal logo center */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:gap-6 items-stretch justify-between w-full max-w-[860px]" data-testid="registration-cards">
            <button
              onClick={() => setShowForm("org")}
              className="csc-card-glass csc-card-hover text-left p-6 flex-1 max-w-[280px] mx-auto sm:mx-0 group"
              style={{ borderColor: "rgba(0,170,255,0.25)", boxShadow: "var(--glow-blue)" }}
              data-testid="org-card-trigger"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(0,170,255,0.15)" }}>
                <Building2 size={20} className="text-[#00aaff]" />
              </div>
              <h2 className="text-[20px] font-bold text-white flex items-center gap-2">🏢 Organization</h2>
              <p className="mt-2 text-[14px] text-[#94a3b8] leading-relaxed">
                Register your charity or non-profit organization to receive free CyberSecurity support, tools, and community resources.
              </p>
              <div className="mt-5 csc-btn-primary w-full" data-testid="org-cta">
                Get Started <ArrowRight size={16} />
              </div>
            </button>

            <button
              onClick={() => setShowForm("person")}
              className="csc-card-glass csc-card-green csc-card-hover text-left p-6 flex-1 max-w-[280px] mx-auto sm:mx-0 group"
              style={{ borderColor: "rgba(57,255,20,0.25)" }}
              data-testid="person-card-trigger"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(57,255,20,0.15)" }}>
                <User size={20} className="text-[#39ff14]" />
              </div>
              <h2 className="text-[20px] font-bold text-white flex items-center gap-2">👤 People</h2>
              <p className="mt-2 text-[14px] text-[#94a3b8] leading-relaxed">
                Join as an individual — volunteer, advocate, or cybersecurity professional. Support charities and connect with a global community.
              </p>
              <div className="mt-5 csc-btn-secondary w-full" data-testid="person-cta">
                Join Free <ArrowRight size={16} />
              </div>
            </button>
          </div>

          {/* Tagline */}
          <div className="mt-10 text-[12px] tracking-[0.32em] uppercase text-[#94a3b8]" data-testid="tagline">
            Securing Today. Empowering Tomorrow.
          </div>

          <p className="mt-3 text-sm text-[#94a3b8]">
            Already have an account?{" "}
            <Link to="/login" className="csc-link" data-testid="hero-signin-link">Log In</Link>
          </p>

          {/* Feature cards */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-5xl" data-testid="feature-cards">
            <FeatureCard
              icon={<Shield size={22} className="text-[#00aaff]" />}
              title="Security Tools"
              body="Professional-grade cybersecurity tools tailored for non-profits."
              testid="feature-tools"
            />
            <FeatureCard
              icon={<Users size={22} className="text-[#00aaff]" />}
              title="Community Network"
              body="Connect with charities worldwide, share knowledge and resources."
              testid="feature-network"
            />
            <FeatureCard
              icon={<Zap size={22} className="text-[#00aaff]" />}
              title="24/7 Support"
              body="Dedicated incident response and cybersecurity guidance."
              testid="feature-support"
            />
          </div>
        </main>

        <footer
          className="px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-4 text-xs text-[#94a3b8]"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div>© {new Date().getFullYear()} CyberSecurity4Charities.org</div>
          <div className="flex gap-5">
            <Link to="/terms" className="hover:text-[#00aaff]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#00aaff]">Privacy</Link>
            <Link to="/about" className="hover:text-[#00aaff]">About / Contact</Link>
          </div>
        </footer>
      </div>

      {/* Registration modals */}
      <Modal open={showForm === "org"} onClose={() => setShowForm(null)} className="max-w-2xl p-0" data-testid="org-form-modal" ariaLabel="Organization registration">
        <div className="p-6">
          <OrgForm refCode={refCode} referrer={referrer} />
        </div>
      </Modal>
      <Modal open={showForm === "person"} onClose={() => setShowForm(null)} className="max-w-2xl p-0" data-testid="person-form-modal" ariaLabel="Person registration">
        <div className="p-6">
          <PersonForm refCode={refCode} referrer={referrer} />
        </div>
      </Modal>
    </div>
  );
}

function FeatureCard({ icon, title, body, testid }) {
  return (
    <div
      className="csc-card csc-card-hover p-6 text-left"
      style={{ borderColor: "rgba(255,255,255,0.07)", boxShadow: "none" }}
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "rgba(0,170,255,0.1)" }}>
        {icon}
      </div>
      <h3 className="text-base font-bold text-white">{title}</h3>
      <p className="mt-2 text-sm text-[#94a3b8]">{body}</p>
    </div>
  );
}
